import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

const pick = (items) => items[Math.floor(Math.random() * items.length)];

function parsePower(raw) {
  const match = String(raw || "").replace(/\s/g, "").match(/^(\d+)\^(\d+)$/);
  if (!match) return null;
  return { base: Number(match[1]), exponent: Number(match[2]) };
}

function powerLatex(base, exponent) {
  return exponent === 1 ? String(base) : `${base}^{${exponent}}`;
}

export function generateTask(options = {}) {
  const mode = options.mode === "quotient" ? "quotient" : "product";
  const base = pick([2, 3, 5]);
  const derivedPower = pick([2, 3]);
  const derivedBase = base ** derivedPower;
  const firstExponent = pick([2, 3, 4]);
  const convertedExponent = derivedPower * firstExponent;

  let secondExponent;
  let resultExponent;

  if (mode === "quotient") {
    secondExponent = pick(
      Array.from({ length: Math.min(5, convertedExponent - 1) }, (_, index) => index + 1)
    );
    resultExponent = convertedExponent - secondExponent;
  } else {
    secondExponent = pick([1, 2, 3, 4]);
    resultExponent = convertedExponent + secondExponent;
  }

  const firstLatex = powerLatex(derivedBase, firstExponent);
  const secondLatex = powerLatex(base, secondExponent);
  const leftLatex = mode === "quotient"
    ? `\\dfrac{${firstLatex}}{${secondLatex}}`
    : `${firstLatex}\\cdot ${secondLatex}`;

  return {
    type: "PowerFamilyPreparationTask",
    data: {
      mode,
      base,
      derivedBase,
      derivedPower,
      firstExponent,
      secondExponent,
      convertedExponent,
      resultExponent,
      leftLatex,
    },
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <p>
        Przedstaw w postaci jednej potęgi o podstawie{" "}
        <InlineMath math={String(data.base)} />:
      </p>
      <BlockMath math={data.leftLatex} />
    </div>
  );
}

export function renderInput(task, userAnswer, onChange, disabled) {
  return (
    <FractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      placeholder={`np. ${task.data.base}^7`}
      label=""
      powerPreview
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  const parsed = parsePower(userAnswer?.raw);
  return Boolean(
    parsed
    && parsed.base === data.base
    && parsed.exponent === data.resultExponent
  );
}

export function renderAnswer({ data }) {
  return <InlineMath math={`${data.base}^{${data.resultExponent}}`} />;
}

export function renderUserAnswer(userAnswer) {
  const parsed = parsePower(userAnswer?.raw);
  if (!parsed) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={`${parsed.base}^{${parsed.exponent}}`} />;
}

export function renderExplanation({ data }) {
  const operation = data.mode === "quotient" ? "-" : "+";

  return (
    <div style={{ display: "grid", rowGap: 8 }}>
      <p>
        <strong>Krok 1.</strong> Sprowadzamy obie liczby do tej samej podstawy.
      </p>
      <BlockMath
        math={`${data.derivedBase}=${data.base}^{${data.derivedPower}},\\qquad
          ${data.derivedBase}^{${data.firstExponent}}
          =\\left(${data.base}^{${data.derivedPower}}\\right)^{${data.firstExponent}}
          =${data.base}^{${data.convertedExponent}}`}
      />
      <p>
        <strong>Krok 2.</strong>{" "}
        {data.mode === "quotient"
          ? "Przy dzieleniu odejmujemy wykładniki."
          : "Przy mnożeniu dodajemy wykładniki."}
      </p>
      <BlockMath
        math={`${data.leftLatex}
          =${data.base}^{${data.convertedExponent}${operation}${data.secondExponent}}
          =${data.base}^{${data.resultExponent}}`}
      />
    </div>
  );
}
